/**
 * Shared error taxonomy for the offline sync queues (orders, answer-syncs).
 *
 * The `@smeetz/orders` / `@smeetz/custom-field-common` data providers use
 * fetch and throw plain Errors with the status embedded in the message
 * ("HTTP 400: Bad Request", possibly wrapped as "REST API call failed after
 * 3 attempts: HTTP 400: Bad Request") — they never carry an axios-style
 * `error.response`. Classifying on `error.response` alone therefore marked
 * every backend error as a network error and retried it forever.
 */
export enum SyncErrorKind {
  /** No HTTP response came back (offline, DNS, timeout, unreachable).
   * Transient — retry forever without consuming the attempt budget. */
  Network = "NETWORK",
  /** The payload is rejected by the backend (4xx). Retrying the identical
   * payload can never succeed — mark the row Ignored and stop. */
  Validation = "VALIDATION",
  /** Any other backend failure (5xx, auth, rate limit). Retry within the
   * attempt budget. */
  Backend = "BACKEND",
}

/** 4xx statuses that are NOT payload errors: auth (may succeed after token
 * refresh), request timeout and rate limiting (transient). */
const RETRYABLE_4XX = [401, 403, 408, 429];

/**
 * Classify a sync failure into network / validation / backend.
 *
 * @param error the error thrown by the data provider or an axios client
 */
export function classifySyncError(error: unknown): SyncErrorKind {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;

  // Axios-shaped errors carry the status directly; the package's fetch-based
  // providers embed it in the message.
  const httpMatch = /HTTP (\d{3})/.exec(String(err?.message ?? ""));
  const status: number | undefined =
    err?.response?.status ?? (httpMatch ? Number(httpMatch[1]) : undefined);

  if (status === undefined) {
    return SyncErrorKind.Network;
  }

  if (status >= 400 && status < 500 && !RETRYABLE_4XX.includes(status)) {
    return SyncErrorKind.Validation;
  }

  return SyncErrorKind.Backend;
}
